import React, { useMemo } from 'react';
import {
  type DimensionValue,
  Pressable,
  type PressableProps,
  type StyleProp,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native';

import { isNotNil } from '../utils';
import { ColorValues } from '@banhala/ui-kit';

export type BoxProps = {
  ref?: React.Ref<View>;
  children?: React.ReactNode;
  horizontal?: boolean;
  reverse?: boolean;
  margin?: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
    horizontal?: DimensionValue;
    vertical?: number;
  };
  padding?: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
    horizontal?: number;
    vertical?: number;
  };
  onStartShouldSetResponder?: ViewProps['onStartShouldSetResponder'];
  width?: ViewStyle['width'];
  minWidth?: ViewStyle['minWidth'];
  maxWidth?: ViewStyle['maxWidth'];
  height?: ViewStyle['height'];
  minHeight?: ViewStyle['minHeight'];
  maxHeight?: ViewStyle['maxHeight'];
  flex?: number;
  position?: ViewStyle['position'];
  top?: ViewStyle['top'];
  bottom?: ViewStyle['bottom'];
  left?: ViewStyle['left'];
  right?: ViewStyle['right'];
  onPress?: PressableProps['onPress'];
  onPressIn?: PressableProps['onPressIn'];
  onLongPress?: () => void;
  borderRadius?: number;
  disabled?: boolean;
  borderTopLeftRadius?: number;
  borderTopRightRadius?: number;
  borderBottomLeftRadius?: number;
  borderBottomRightRadius?: number;
  borderWidth?: number;
  borderTopWidth?: number;
  borderBottomWidth?: number;
  borderColor?: keyof typeof ColorValues | 'transparent';
  backgroundColor?: keyof typeof ColorValues | 'transparent';
  backgroundColorOpacity?: number;
  opacity?: number;
  pointerEvents?: ViewProps['pointerEvents'];
  flexWrap?: ViewStyle['flexWrap'];
  alignItems?: ViewStyle['alignItems'];
  justifyContent?: ViewStyle['justifyContent'];
  transform?: ViewStyle['transform'];
  overflow?: ViewStyle['overflow'];
  onLayout?: ViewProps['onLayout'];
  alignSelf?: ViewStyle['alignSelf'];
  hitSlop?: PressableProps['hitSlop'];
  zIndex?: number;
  shadowStep?: 1 | 2 | 3 | 4 | 5;
  gap?: number;
};

export const getShadowStyle = (elevation: 1 | 2 | 3 | 4 | 5) => {
  switch (elevation) {
    case 1:
      return {
        shadowColor: ColorValues.black,
        shadowOffset: {
          width: 0,
          height: 1,
        },
        shadowOpacity: 0.12,
        shadowRadius: 1.0,

        elevation: 1,
      };
    case 2:
      return {
        shadowColor: ColorValues.black,
        shadowOffset: {
          width: 0,
          height: 1,
        },
        shadowOpacity: 0.12,
        shadowRadius: 1.41,

        elevation: 2,
      };
    case 3:
      return {
        shadowColor: ColorValues.black,
        shadowOffset: {
          width: 0,
          height: 1,
        },
        shadowOpacity: 0.15,
        shadowRadius: 2.22,

        elevation: 3,
      };
    case 4:
      return {
        shadowColor: ColorValues.black,
        shadowOffset: {
          width: 0,
          height: 2,
        },
        shadowOpacity: 0.16,
        shadowRadius: 2.62,

        elevation: 4,
      };
    case 5:
      return {
        shadowColor: ColorValues.black,
        shadowOffset: {
          width: 0,
          height: 2,
        },
        shadowOpacity: 0.35,
        shadowRadius: 3.84,

        elevation: 5,
      };
  }
};

const Box = ({ ref, ...props }: BoxProps) => {
  const viewStyle = useMemo<StyleProp<ViewStyle>>(() => {
    const getFlexDirection = () => {
      if (props.reverse) {
        return props.horizontal ? 'row-reverse' : 'column-reverse';
      }

      return props.horizontal ? 'row' : 'column';
    };
    return [
      {
        flexDirection: getFlexDirection(),
        flex: props.flex,
        flexWrap: props.flexWrap,
        maxWidth: props.maxWidth,
        minWidth: props.minWidth,
        maxHeight: props.maxHeight,
        minHeight: props.minHeight,
        width: props.width,
        height: props.height,
        backgroundColor: props.backgroundColor,
      },
      { zIndex: props.zIndex, transform: props.transform },
      {
        borderWidth: props.borderWidth,
        borderRadius: props.borderRadius,
        borderTopLeftRadius: props.borderTopLeftRadius,
        borderTopRightRadius: props.borderTopRightRadius,
        borderBottomLeftRadius: props.borderBottomLeftRadius,
        borderBottomRightRadius: props.borderBottomRightRadius,
        borderTopWidth: props.borderTopWidth,
        borderBottomWidth: props.borderBottomWidth,
        borderColor: isNotNil(props.borderColor)
          ? props.borderColor !== 'transparent'
            ? ColorValues[props.borderColor]
            : 'transparent'
          : undefined,
      },
      {
        marginTop: props.margin?.top,
        marginBottom: props.margin?.bottom,
        marginLeft: props.margin?.left,
        marginRight: props.margin?.right,
        marginHorizontal: props.margin?.horizontal,
        marginVertical: props.margin?.vertical,
        paddingTop: props.padding?.top,
        paddingBottom: props.padding?.bottom,
        paddingRight: props.padding?.right,
        paddingLeft: props.padding?.left,
        paddingHorizontal: props.padding?.horizontal,
        paddingVertical: props.padding?.vertical,
      },
      {
        alignItems: props.alignItems,
        alignSelf: props.alignSelf,
        justifyContent: props.justifyContent,
        overflow: props.overflow,
        opacity: props.opacity,
      },
      props.shadowStep ? getShadowStyle(props.shadowStep) : {},
      {
        position: props.position,
        top: props.top,
        bottom: props.bottom,
        right: props.right,
        left: props.left,
        zIndex: props.zIndex,
      },
      { gap: props.gap },
    ];
  }, [props]);
  if (isNotNil(props.onPress)) {
    return (
      <Pressable
        ref={ref}
        onPress={props.onPress}
        onPressIn={props.onPressIn}
        onLongPress={props.onLongPress}
        style={viewStyle}
        onLayout={props.onLayout}
        hitSlop={props.hitSlop}
        disabled={props.disabled}
        pointerEvents={props.pointerEvents}
      >
        {props.children}
      </Pressable>
    );
  }
  return (
    <View
      ref={ref}
      style={viewStyle}
      onLayout={props.onLayout}
      pointerEvents={props.pointerEvents}
    >
      {props.children}
    </View>
  );
};

export const HorizontalBox = (props: Omit<BoxProps, 'horizontal'>) => {
  return (
    <Box {...props} horizontal={true}>
      {props.children}
    </Box>
  );
};

export const VerticalBox = (props: Omit<BoxProps, 'horizontal'>) => {
  return (
    <Box {...props} horizontal={false}>
      {props.children}
    </Box>
  );
};
// Box.displayName = 'Box';

HorizontalBox.displayName = 'HorizontalBox';
VerticalBox.displayName = 'VerticalBox';
